/**
 * Vitals rules: pure calculations and range flagging.
 *
 * Only BMI and blood pressure are colour-coded, using BestNotes Classic's Color Key with the gaps
 * closed (#6014 "Range flagging"). The category is stored at save as `interpretation` (a BestNotes
 * coding plus its HL7 ObservationInterpretation equivalent) with a `referenceRange` for Normal.
 */
import { differenceInYears, isValid, parseISO } from 'date-fns';
import { UCUM_SYSTEM, VITALS_BY_KEY, VITALS_INTERPRETATION_SYSTEM } from '@/common/vitalsConstants';

const HL7_INTERPRETATION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation';
const REFERENCE_RANGE_MEANING_SYSTEM = 'http://terminology.hl7.org/CodeSystem/referencerange-meaning';
export const ADULT_BMI_AGE = 20;

const CATEGORIES = {
    underweight: { display: 'Underweight', hl7: 'L' },
    normal: { display: 'Normal', hl7: 'N' },
    overweight: { display: 'Overweight', hl7: 'H' },
    obesity: { display: 'Obesity', hl7: 'H' },
    'bp-low': { display: 'Low', hl7: 'L' },
    'bp-normal': { display: 'Normal', hl7: 'N' },
    'bp-elevated': { display: 'Elevated', hl7: 'H' },
    'bp-stage-1': { display: 'High BP Stage 1', hl7: 'H' },
    'bp-stage-2': { display: 'High BP Stage 2', hl7: 'H' },
    'bp-crisis': { display: 'Hypertensive Crisis', hl7: 'HH' },
};

const HL7_DISPLAY = { L: 'Low', N: 'Normal', H: 'High', HH: 'Critical high' };

// The category boundaries, the one source for both the categorising (bmiCategory, bpCategory, isOrthostaticDrop) and
// the Color Key ranges people read in the tooltip, so the two can't drift apart.
// BMI: each limit is where that band starts.
const BMI_LIMITS = { normal: 18.5, overweight: 25, obesity: 30 };
// Blood pressure (mmHg): low is below either number, elevated is systolic at or above, stage 1 and 2 start at either
// number, crisis is above either.
const BP_LIMITS = {
    low: { systolic: 90, diastolic: 60 },
    elevated: { systolic: 120 },
    stage1: { systolic: 130, diastolic: 80 },
    stage2: { systolic: 140, diastolic: 90 },
    crisis: { systolic: 180, diastolic: 120 },
};
// A standing reading falling at least this far against the one it's compared with.
const ORTHOSTATIC_DROP = { systolic: 20, diastolic: 10 };

// The Color Key rows as people read them, written from the limits above.
const bmiBand = (code, range) => ({ code, display: CATEGORIES[code].display, range });
const bpBand = (code, systolic, joiner, diastolic) => ({ code, display: CATEGORIES[code].display, systolic, joiner, diastolic });
const bmiBelow = (limit) => (Math.round(limit * 10) - 1) / 10; // BMI is kept to one decimal, so a band ends 0.1 below the next
const { low, elevated, stage1, stage2, crisis } = BP_LIMITS;

export const CATEGORY_RANGES = {
    bmi: {
        note: `Adults ${ADULT_BMI_AGE} and older`,
        bands: [
            bmiBand('underweight', `< ${BMI_LIMITS.normal}`),
            bmiBand('normal', `${BMI_LIMITS.normal}–${bmiBelow(BMI_LIMITS.overweight)}`),
            bmiBand('overweight', `${BMI_LIMITS.overweight}–${bmiBelow(BMI_LIMITS.obesity)}`),
            bmiBand('obesity', `≥ ${BMI_LIMITS.obesity}`),
        ],
    },
    bloodPressure: {
        note: 'The most severe band either number reaches applies',
        bands: [
            bpBand('bp-low', `< ${low.systolic}`, 'or', `< ${low.diastolic}`),
            bpBand('bp-normal', `< ${elevated.systolic}`, 'and', `< ${stage1.diastolic}`),
            bpBand('bp-elevated', `${elevated.systolic}–${stage1.systolic - 1}`, 'and', `< ${stage1.diastolic}`),
            bpBand('bp-stage-1', `${stage1.systolic}–${stage2.systolic - 1}`, 'or', `${stage1.diastolic}–${stage2.diastolic - 1}`),
            bpBand('bp-stage-2', `≥ ${stage2.systolic}`, 'or', `≥ ${stage2.diastolic}`),
            bpBand('bp-crisis', `> ${crisis.systolic}`, 'or', `> ${crisis.diastolic}`),
        ],
    },
    'orthostatic-drop': {
        display: 'Orthostatic drop',
        text: `Standing systolic falls ${ORTHOSTATIC_DROP.systolic} or more, or diastolic ${ORTHOSTATIC_DROP.diastolic} or more, against lying (or sitting when there is no lying reading)`,
    },
};

const toNumber = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));
const isNumber = (value) => Number.isFinite(toNumber(value));
const isPositive = (value) => isNumber(value) && toNumber(value) > 0;
export const roundTo1 = (value) => Math.round(value * 10) / 10;

/** A value nothing was entered for: undefined, null or a blank string. Numbers (including 0) are never blank. */
export const isBlank = (value) => value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

const quantity = (value, code) => ({ value, unit: code, system: UCUM_SYSTEM, code });

export function feetInchesToInches(feet, inches) {
    return (Number(feet) || 0) * 12 + (Number(inches) || 0);
}

export function calculateBmi(heightIn, weightLb) {
    if (!isPositive(heightIn) || !isPositive(weightLb)) return null;
    return roundTo1((703 * toNumber(weightLb)) / toNumber(heightIn) ** 2);
}

export function calculatePercentOf(weightLb, targetLb) {
    if (!isPositive(weightLb) || !isPositive(targetLb)) return null;
    return roundTo1((toNumber(weightLb) / toNumber(targetLb)) * 100);
}

export function ageInYears(birthDate, atDateTime) {
    if (!birthDate) return null;
    const born = birthDate instanceof Date ? birthDate : parseISO(birthDate);
    const at = atDateTime ? new Date(atDateTime) : new Date();
    if (!isValid(born) || !isValid(at)) return null;
    return differenceInYears(at, born);
}

export function bmiCategory(bmi, age) {
    if (!isNumber(bmi) || !isNumber(age) || toNumber(age) < ADULT_BMI_AGE) return null;
    bmi = toNumber(bmi);
    if (bmi < BMI_LIMITS.normal) return 'underweight';
    if (bmi < BMI_LIMITS.overweight) return 'normal';
    if (bmi < BMI_LIMITS.obesity) return 'overweight';
    return 'obesity';
}

// The most severe category either number reaches wins; Low applies only when nothing is Elevated or higher.
export function bpCategory(systolic, diastolic) {
    if (!isNumber(systolic) || !isNumber(diastolic)) return null;
    systolic = toNumber(systolic);
    diastolic = toNumber(diastolic);
    if (systolic > crisis.systolic || diastolic > crisis.diastolic) return 'bp-crisis';
    if (systolic >= stage2.systolic || diastolic >= stage2.diastolic) return 'bp-stage-2';
    if (systolic >= stage1.systolic || diastolic >= stage1.diastolic) return 'bp-stage-1';
    if (systolic >= elevated.systolic) return 'bp-elevated';
    if (systolic < low.systolic || diastolic < low.diastolic) return 'bp-low';
    return 'bp-normal';
}

export function isOrthostaticDrop(standing, reference) {
    if (!isNumber(standing?.systolic) || !isNumber(standing?.diastolic) || !isNumber(reference?.systolic) || !isNumber(reference?.diastolic)) return false;
    return toNumber(reference.systolic) - toNumber(standing.systolic) >= ORTHOSTATIC_DROP.systolic || toNumber(reference.diastolic) - toNumber(standing.diastolic) >= ORTHOSTATIC_DROP.diastolic;
}

// Standing is compared against lying, or against sitting when the set has no lying reading.
export function orthostaticReference(bpByPosition = {}) {
    const complete = (bp) => isNumber(bp?.systolic) && isNumber(bp?.diastolic);
    if (complete(bpByPosition.bpLying)) return 'bpLying';
    if (complete(bpByPosition.bpSitting)) return 'bpSitting';
    return null;
}

export function interpretationFor(category, { orthostaticDrop } = {}) {
    const coding = [];
    const band = CATEGORIES[category];
    if (band) {
        coding.push({ system: VITALS_INTERPRETATION_SYSTEM, code: category, display: band.display });
        coding.push({ system: HL7_INTERPRETATION_SYSTEM, code: band.hl7, display: HL7_DISPLAY[band.hl7] });
    }
    if (orthostaticDrop) {
        coding.push({ system: VITALS_INTERPRETATION_SYSTEM, code: 'orthostatic-drop', display: 'Orthostatic drop' });
    }
    return coding.length ? [{ coding }] : [];
}

const NORMAL_RANGE_TYPE = { coding: [{ system: REFERENCE_RANGE_MEANING_SYSTEM, code: 'normal', display: 'Normal Range' }] };

/**
 * The Normal reference range. BMI returns ReferenceRange[]; a blood pressure returns
 * { systolic: ReferenceRange[], diastolic: ReferenceRange[] } for its components. Any other key returns [].
 */
export function referenceRangeFor(key) {
    if (key === 'bmi') {
        return [{ low: quantity(BMI_LIMITS.normal, 'kg/m2'), high: quantity(bmiBelow(BMI_LIMITS.overweight), 'kg/m2'), type: NORMAL_RANGE_TYPE, age: { low: quantity(ADULT_BMI_AGE, 'a') } }];
    }
    if (VITALS_BY_KEY[key]?.kind === 'bloodPressure') {
        // Normal is below Elevated's systolic and below Stage 1's diastolic.
        return {
            systolic: [{ high: quantity(elevated.systolic - 1, 'mm[Hg]'), type: NORMAL_RANGE_TYPE }],
            diastolic: [{ high: quantity(stage1.diastolic - 1, 'mm[Hg]'), type: NORMAL_RANGE_TYPE }],
        };
    }
    return [];
}

/** A soft warning only: an implausible value never blocks saving. Never throws. */
export function checkPlausibility(key, value) {
    try {
        const vital = VITALS_BY_KEY[key];
        if (!vital?.plausible || value === undefined || value === null || value === '') return { plausible: true };

        const outOfRange = (number, { min, max }, label) => {
            const parsed = Number(number);
            if (number === undefined || number === null || number === '') return null;
            if (!Number.isFinite(parsed)) return `${label} must be a number`;
            if (parsed < min || parsed > max) return `${label} of ${parsed} ${vital.unit.display} is outside the expected range (${min}-${max})`;
            return null;
        };

        let messages;
        if (vital.kind === 'bloodPressure') {
            messages = [outOfRange(value?.systolic, vital.plausible.systolic, 'Systolic'), outOfRange(value?.diastolic, vital.plausible.diastolic, 'Diastolic')];
        } else if (key === 'height' && typeof value === 'object') {
            if (!isNumber(value.feet) && !isNumber(value.inches)) return { plausible: true };
            messages = [outOfRange(feetInchesToInches(value.feet, value.inches), vital.plausible, vital.label)];
        } else {
            messages = [outOfRange(value, vital.plausible, vital.label)];
        }

        const message = messages.filter(Boolean).join('. ');
        return message ? { plausible: false, message } : { plausible: true };
    } catch (error) {
        console.error('[vitalsRules] checkPlausibility failed', { key, error });
        return { plausible: true };
    }
}
